/**
 * SubscriptionsBundlingPage
 *
 * Subscription management page for billing admins. Branches on
 * `organization_info.group`:
 *   - Group orgs render `GroupSubscriptionOverview` (read-only billing
 *     overview; plan changes happen per-member in the Members tab).
 *   - Solo orgs render `SoloSubscriptionView` — single-page experience
 *     combining tier selection, add-on selection, invite CTA, cancel
 *     subscription, and the sticky `ManagementSidebar`.
 */

import { useEffect, useState } from 'react';
import { useOrganizationInfo } from '@features/user';
import { CheckCircle } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useGetFullSubscriptionDetailsQuery } from './store/api';
import {
  GroupSubscriptionOverview,
  SoloSubscriptionView,
  TrialBanner,
  UpdatePaymentMethodModal,
} from './components';

type StatusSeverity = 'success' | 'info' | 'error' | 'warning';

const getStatusSeverity = (status: string | undefined | null): StatusSeverity => {
  if (!status) return 'info';
  const s = status.toLowerCase();
  if (s === 'active') return 'success';
  if (s === 'trialing') return 'info';
  if (s === 'canceled' || s === 'cancelled') return 'error';
  if (s === 'past_due' || s === 'paused' || s === 'unpaid') return 'warning';
  return 'info';
};

const requiresPaymentAction = (status: string | undefined | null): boolean => {
  if (!status) return false;
  const s = status.toLowerCase();
  return ['past_due', 'paused', 'unpaid', 'canceled', 'cancelled'].includes(s);
};

const getPaymentButtonText = (status: string | undefined | null): string => {
  if (!status) return 'Add payment method';
  switch (status.toLowerCase()) {
    case 'past_due':
      return 'Update payment';
    case 'paused':
      return 'Resume subscription';
    case 'unpaid':
      return 'Fix payment';
    case 'canceled':
    case 'cancelled':
      return 'Reactivate';
    default:
      return 'Add payment method';
  }
};

const getPaymentButtonColor = (severity: StatusSeverity): 'primary' | 'warning' | 'error' => {
  if (severity === 'warning') return 'warning';
  if (severity === 'error') return 'error';
  return 'primary';
};

export default function SubscriptionsBundlingPage() {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const { subscriptionManagement } = useEventTracking();

  const {
    data: subscriptionDetails,
    isLoading: isLoadingSubscription,
    error: subscriptionError,
    refetch: refetchSubscription,
  } = useGetFullSubscriptionDetailsQuery();

  const isGroup = useOrganizationInfo()?.group ?? false;

  const handlePaymentMethodUpdate = () => {
    subscriptionManagement.paymentMethodUpdated();
    refetchSubscription();
  };

  const handleSubscriptionStatusChange = () => {
    refetchSubscription();
  };

  const handleOpenPaymentModal = () => {
    subscriptionManagement.paymentMethodUpdateClicked();
    setShowPaymentModal(true);
  };

  // Page-view + discount tracking — fires once after the subscription loads.
  useEffect(() => {
    if (isLoadingSubscription || !subscriptionDetails) return;

    const discount = subscriptionDetails.discount;
    const hasDiscount = !!discount && (!!discount.percent_off || !!discount.amount_off);

    subscriptionManagement.pageViewed({
      subscription_status: subscriptionDetails.subscription_status,
      billing_interval: subscriptionDetails.billing_interval,
      has_payment_method: !!subscriptionDetails.payment_method,
      is_trialing: subscriptionDetails.is_trialing,
      has_discount: hasDiscount,
      discount_type: hasDiscount ? (discount?.percent_off ? 'percent' : 'fixed') : undefined,
      discount_duration: hasDiscount ? (discount?.duration ?? undefined) : undefined,
    });

    if (hasDiscount) {
      subscriptionManagement.discountDisplayed({
        discount_type: discount?.percent_off ? 'percent' : 'fixed',
        discount_duration: discount?.duration ?? undefined,
        coupon_name: discount?.coupon_name ?? undefined,
      });
    }
  }, [isLoadingSubscription, subscriptionDetails, subscriptionManagement]);

  const needsPaymentMethod =
    subscriptionDetails &&
    (requiresPaymentAction(subscriptionDetails.subscription_status) ||
      !subscriptionDetails.payment_method);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, minHeight: '100%' }}>
      {isLoadingSubscription ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : subscriptionError ? (
        <Alert severity="warning">
          Unable to load subscription details. Please try again later.
        </Alert>
      ) : subscriptionDetails && subscriptionDetails.subscription_status ? (
        <>
          {/* Trialing orgs see the richer TrialBanner (days remaining + smart
              "Choose a tier below" suffix + prominent payment CTA). Non-trial
              orgs keep the terse status Alert — only shown when payment action
              is required or the status is non-active. Active orgs without
              issues don't need a banner cluttering the view. */}
          {subscriptionDetails.is_trialing ? (
            <TrialBanner
              subscription={subscriptionDetails}
              onUpdatePaymentMethod={handleOpenPaymentModal}
            />
          ) : (
            needsPaymentMethod ||
            getStatusSeverity(subscriptionDetails.subscription_status) !== 'success'
          ) ? (
            <Alert
              severity={getStatusSeverity(subscriptionDetails.subscription_status)}
              icon={<CheckCircle />}
              action={
                needsPaymentMethod ? (
                  <Button
                    variant="contained"
                    size="small"
                    color={getPaymentButtonColor(
                      getStatusSeverity(subscriptionDetails.subscription_status),
                    )}
                    onClick={handleOpenPaymentModal}
                    sx={{ minWidth: 140, fontWeight: 600 }}
                  >
                    {getPaymentButtonText(subscriptionDetails.subscription_status)}
                  </Button>
                ) : undefined
              }
              sx={{
                borderRadius: 2,
                border: 1,
                borderColor: theme =>
                  theme.palette[getStatusSeverity(subscriptionDetails.subscription_status)].main,
                '& .MuiAlert-action': { marginRight: 0 },
              }}
            >
              <Typography component="span" sx={{ fontWeight: 600 }}>
                {subscriptionDetails.subscription_status.charAt(0).toUpperCase() +
                  subscriptionDetails.subscription_status.slice(1)}
              </Typography>
            </Alert>
          ) : null}

          {isGroup ? (
            <GroupSubscriptionOverview
              subscription={subscriptionDetails}
              onUpdatePaymentMethod={handleOpenPaymentModal}
              onSubscriptionStatusChange={handleSubscriptionStatusChange}
            />
          ) : (
            <SoloSubscriptionView
              subscription={subscriptionDetails}
              onUpdatePaymentMethod={handleOpenPaymentModal}
              onSubscriptionStatusChange={handleSubscriptionStatusChange}
            />
          )}
        </>
      ) : subscriptionDetails && !subscriptionDetails.subscription_status ? (
        <Card elevation={2} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No Active Subscription
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You don&apos;t have an active subscription at this time.
          </Typography>
        </Card>
      ) : null}

      <UpdatePaymentMethodModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentMethodUpdate}
        existingBillingDetails={subscriptionDetails?.billing_details}
      />
    </Box>
  );
}
